import _, { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { useDashboard } from '../context/dashboard-context';
import { useUser } from '../context/user-context';
import type { CellObject } from '../features/overview-table/hooks/use-table';
import type { Forecast } from '../graphql/types';
import { sameTimeYearsAgo } from '../helpers/date-helpers';
import { calculateForecast } from '../helpers/forecast-helpers';
import { isValidRate, MAX_RATE, MIN_RATE } from '../helpers/rate-shop-helpers';
import { useConfirmDialog } from '../hooks/use-confirm-dialog';
import { useGridCell } from '../hooks/use-grid-cell';

type Props = {
  cell: CellObject;
  columnIndex: number;
  isActiveCell: boolean;
  rowIndex: number;
};

const MAX_LENGTH = 10;

const RenderEditable = ({ cell, columnIndex, isActiveCell, rowIndex }: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [edit, setEdit] = useState(false);
  const [value, setValue] = useState(cell.value || '');
  const { hotel, hotelSettings, setActiveCell } = useDashboard();
  const { user } = useUser();
  const callback = useCallback(
    () =>
      debounce(
        () => {
          ReactTooltip.rebuild();
          console.count('rebuild');
        },
        500,
        {
          leading: false,
          trailing: true,
        },
      ),
    [],
  );
  const { forecasts, notes, rmRecs } = useGridCell({ callback });
  const { confirm, dialog } = useConfirmDialog();
  // True while the rate dialog is open, so the input's blur doesn't save before the user answers.
  const confirmingRef = useRef(false);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.focus();
      inputRef.current.type === 'text' && inputRef.current.select();
    }
    if (edit === true) {
      setActiveCell({ row: rowIndex, col: columnIndex });
    }
  }, [edit]);

  useEffect(() => {
    setValue(cell.value || '');
  }, [cell.value]);

  useEffect(() => {
    isActiveCell && handleSetEdit();
  }, [isActiveCell]);

  const cleanValue = (value: string | number) => {
    return typeof value === 'string' ? value.replace('$', '') : value;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;

    switch (key) {
      case 'ArrowDown':
        saveAndMove({ row: rowIndex + 1, col: columnIndex });
        break;
      case 'ArrowLeft':
        if (inputRef.current?.selectionStart === 0) {
          saveAndMove({ row: rowIndex, col: columnIndex - 1 });
        }
        break;
      case 'ArrowUp':
        saveAndMove({ row: rowIndex - 1, col: columnIndex });
        break;
      case 'ArrowRight':
        // We only want to move cells to the right if the user
        // is at the end of the value. This prevents it from moving cells
        // if they are only using the arrow keys to navigate within the input.
        if (inputRef.current?.selectionEnd === inputRef.current?.value.length) {
          saveAndMove({ row: rowIndex, col: columnIndex + 1 });
        }
        break;
      case 'Delete':
        setValue('');
        break;
      case 'Enter':
        // Stop the browser from also delivering this Enter to the rate dialog's focused button,
        // which would accept the dialog the instant it opens.
        e.preventDefault();
        saveAndMove({ row: rowIndex + 1, col: columnIndex });
        break;
      case 'Escape':
        setEdit(false);
        setValue(cell.value as string);
        break;
      case 'Tab':
        // Keep focus in this cell instead of tabbing away while the rate dialog is open.
        if (needsRateConfirm(value)) e.preventDefault();
        saveAndMove({ row: rowIndex, col: columnIndex + 1 });
        break;
      default:
        break;
    }
  };

  /**
   * Save the value and move to the next cell. Rates outside the valid range are only saved
   * after the user confirms; cancelling keeps the cell in edit mode.
   */
  const saveAndMove = async (nextCell: { row: number; col: number }) => {
    if (needsRateConfirm(value) && !(await confirmRate())) return;
    setActiveCell(nextCell);
    handleSave();
  };

  /**
   * Ask before saving a rate outside the valid range. Cancelling keeps the cell in edit mode.
   */
  const confirmRate = async () => {
    confirmingRef.current = true;
    const confirmed = await confirm({
      title: 'Rate outside valid range',
      message: `$${cleanValue(value)} is outside the valid rate range of $${MIN_RATE} to $${MAX_RATE}. The rate can be saved but will not be uploaded.`,
      confirmLabel: 'Save anyway',
      variant: 'warning',
    });
    confirmingRef.current = false;
    if (!confirmed) {
      inputRef.current?.focus();
    }
    return confirmed;
  };

  const handleBlur = () => {
    // Opening the rate dialog moves focus out of the input; the dialog decides whether to save.
    if (confirmingRef.current) return;
    handleSave();
  };

  const handleSave = () => {
    setEdit(false);
    if (valuesAreNew()) {
      console.log('saving new value', cleanValue(value));
      switch (true) {
        case cell.dataKey.includes('rm_recs.'):
          if (cell.meta?.id) {
            // editing an accepted rate re-opens the rec for review
            const reopenRec =
              cell.dataKey === 'rm_recs.rate' && cell.meta?.data?.status === 'ACCEPTED';

            // update existing rm_recs
            rmRecs.update[0]({
              variables: {
                updateRmRecId: cell.meta.id,
                rmRec: {
                  [cell.dataKey.split('.')[1]]: cell.dataKey.includes('rate')
                    ? Number(value)
                    : value,
                  ...(reopenRec ? { status: 'OPEN' } : {}),
                },
              },
            });
          } else {
            // create new rm_recs
            rmRecs.create[0]({
              variables: {
                rmRec: {
                  created_by_id: user?.id,
                  hotel_id: hotel?.hotel_id || hotel?.brand_code,
                  stay_date: cell.stayDate,
                  [cell.dataKey.split('.')[1]]: cell.dataKey.includes('rate')
                    ? Number(value)
                    : value,
                },
              },
            });
          }
          break;
        case cell.dataKey.includes('forecast'):
          if (cell.meta?.id) {
            const existingForecast = cell.meta?.data as Forecast;

            const forecastValues = calculateForecast({
              ...existingForecast,
              [cell.dataKey]: Number(value),
            });

            // update existing forecast
            forecasts.update[0]({
              variables: {
                updateForecastId: cell.meta.id,
                forecast: {
                  ..._.pick(forecastValues, [
                    'forecast_sold_1',
                    'forecast_sold_2',
                    'forecast_adr_1',
                    'forecast_adr_2',
                  ]),
                },
              },
              optimisticResponse: {
                updateForecast: {
                  __typename: 'Forecast',
                  ...forecastValues,
                },
              },
            });
          } else {
            // create new forecast
            const existingForecast = cell.meta?.data as Forecast;

            const forecastValues = calculateForecast({
              ...existingForecast,
              [cell.dataKey]: Number(value),
            });

            forecasts.create[0]({
              variables: {
                forecast: {
                  created_by_id: user?.id,
                  hotel_id: hotel?.hotel_id || hotel?.brand_code,
                  stay_date: cell.stayDate,
                  ..._.pick(forecastValues, [
                    'forecast_sold_1',
                    'forecast_sold_2',
                    'forecast_adr_1',
                    'forecast_adr_2',
                  ]),
                  [cell.dataKey]: Number(value),
                },
              },
              optimisticResponse: {
                createForecast: {
                  __typename: 'Forecast',
                  created_by_id: user?.id,
                  hotel_id: hotel?.hotel_id || hotel?.brand_code,
                  stay_date: cell.stayDate,
                  ...forecastValues,
                  [cell.dataKey]: Number(value),
                },
              },
            });
          }
          break;
        case cell.dataKey.includes('note_'):
          if (cell.meta?.id) {
            // update existing notes
            notes.update[0]({
              variables: {
                updateNoteId: cell.meta.id,
                note: {
                  content: value,
                },
              },
            });
          } else {
            // create new note
            notes.create[0]({
              variables: {
                note: {
                  created_by_id: user?.id,
                  hotel_id: hotel?.hotel_id || hotel?.brand_code,
                  stay_date: cell.dataKey.includes('note_ly')
                    ? sameTimeYearsAgo(cell.stayDate)
                    : cell.stayDate,
                  content: value,
                },
              },
            });
          }
          break;
        default:
          break;
      }
    } else {
      setValue(cell.value || '');
    }
  };

  /**
   * Should the value be truncated?
   * @param cellValue The attribute 'value' from the cell object. Can be string, number, or null.
   * @returns True if the value is not a number and is not null or an empty string.
   */
  const shouldTruncate = (cellValue: typeof cell.value) => {
    return Number.isNaN(Number(cellValue));
  };

  const needsRateConfirm = (value: string | number) =>
    cell.dataKey?.includes('rate') && value !== '' && !isValidRate(Number(cleanValue(value)));

  const valuesAreNew = (): boolean => {
    if (cell.value === null && value === '') {
      return false;
    }
    return String(value) !== String(cell.value);
  };

  const handleSetEdit = () => {
    if (cell.dataKey.includes('note_ly')) {
      setEdit(false);
      return;
    } else {
      setEdit(true);
    }
  };

  const DisplayCell = () => {
    if (shouldTruncate(cell.value)) {
      const displayValue = valuesAreNew() ? value : cell.displayValue || '...';
      return (
        <span data-tip={displayValue} onClick={() => handleSetEdit()}>
          {String(displayValue).length > MAX_LENGTH
            ? String(displayValue).substring(0, MAX_LENGTH)
            : displayValue}
        </span>
      );
    } else if (cell.dataKey.includes('forecast')) {
      const bestrev = cell.meta?.data?.calcForecasts?.bestrev;
      const calcValue = bestrev ? Math.round(bestrev[cell.dataKey]) : undefined;
      const showSuperscript = hotelSettings?.forecasts?.showSuperscript;
      return (
        <span onClick={() => handleSetEdit()}>
          {valuesAreNew() ? value : cell.displayValue || '...'}
          <sup>{showSuperscript && calcValue && !Number.isNaN(calcValue) ? calcValue : ''}</sup>
        </span>
      );
    } else {
      return (
        <span onClick={() => handleSetEdit()}>
          {valuesAreNew() ? value : cell.displayValue || '...'}
        </span>
      );
    }
  };

  return (
    <>
      {edit ? (
        <input
          className='text-center w-full mb-1'
          ref={inputRef}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          type='text'
          value={value}
        />
      ) : (
        <DisplayCell />
      )}
      {dialog}
    </>
  );
};

export default RenderEditable;
